import BodyRoster from './BodyRoster.jsx'
import { useEffect, useState } from 'react'
import { RUNNERS } from './bodyControls.js'
import { api } from '../../lib/api.js'
import styles from './IssueFix.module.css'

/**
 * IssueFix — ONE FLAG, AND THE CONTROL THAT ENDS IT, IN THE SAME BOX.
 *
 * ⚠️ THE COMPONENT CANNOT RENDER A FLAG WITHOUT ITS CONTROLS, and that is the
 * whole design. It does not decide which buttons to draw — it walks
 * `issue.controls`, which `bodyIssues.js` resolved and `assertNoDeadEnds()`
 * already refused to let be empty. A renderer that chose its own buttons could
 * forget one and silently recreate the dead end for that kind alone, with
 * nothing failing anywhere.
 *
 * ⚠️ AND IT REFUSES TO RENDER A CONTROL-LESS ISSUE OUT LOUD. If one ever
 * reaches here it prints as a defect in the board rather than as a quiet
 * sentence — the failure must be visible on the surface that has it.
 *
 * ⚠️ EVERY RUN LEAVES A SENTENCE, INCLUDING THE ZERO. Brian on the mark
 * button: "it doesnt seem to do anything." An empty to-do produced silence,
 * which is indistinguishable from broken. `result.note` is always set by
 * bodyControls.js and is always rendered.
 *
 * ⚠️ MOBILE FIRST, because that is the acceptance test. Controls stack at
 * phone width, hit targets are 44px, and nothing relies on hover — a control
 * you cannot press on a phone is a dead end for the person most likely to be
 * standing in front of a customer.
 */

const SEV = { bad: 'bad', warn: 'warn', muted: 'muted' }

export default function IssueFix({ issue, context = {}, onDone }) {
  const [open, setOpen] = useState(null)      // which verb's form is open
  const [fields, setFields] = useState({})
  const [busy, setBusy] = useState(false)
  const [progress, setProgress] = useState(null)
  const [result, setResult] = useState(null)  // { ok, note, dryRun, ... }
  const [staged, setStaged] = useState(null)  // a dry run that may now be applied
  /* ⚠️ THE OPERATION'S DESCRIPTION COMES FROM THE SERVER, NEVER FROM HERE.
     `/api/repair/system` names each operation and says what it touches and
     why; a copy of that text in the UI would drift from the rule that
     actually runs, and the person pressing the button would be reading a
     description of a different repair. */
  const [ops, setOps] = useState(null)
  const wantsOperation = Boolean(issue?.controls?.some((c) => (c.needs || []).includes('operation')))
  useEffect(() => {
    if (!wantsOperation) return undefined
    const ac = new AbortController()
    let live = true
    api.repairSystemOperations({ signal: ac.signal })
      .then((d) => { if (live) setOps(d?.operations || []) })
      .catch(() => { if (live) setOps([]) })
    return () => { live = false; ac.abort() }
  }, [wantsOperation])

  if (!issue) return null

  /* ⚠️ LOUD, NOT SILENT. See the note above. */
  if (!issue.controls || !issue.controls.length) {
    return (
      <p className={styles.deadEnd} role="alert">
        <b>DEAD END.</b> “{issue.label}” is on the board with no control attached. That is a bug in
        this build, not a property of the data — report it rather than working around it.
      </p>
    )
  }

  const set = (k) => (e) => setFields((f) => ({ ...f, [k]: e.target.value }))

  async function run(verb, dryRun) {
    const runner = RUNNERS[verb]
    if (!runner) {
      setResult({ ok: false, note: `No runner for "${verb}". Nothing was attempted — a button wired to nothing is the defect this surface exists to remove.` })
      return
    }
    setBusy(true); setProgress(null)
    try {
      const args = {
        ...context,
        ...fields,
        records: issue.records || context.records || [],
        bodyKey: issue.bodyKey || context.bodyKey || null,
        operation: fields.operation || issue.operation || context.operation || null,
        rule: fields.rule || issue.rule || context.rule || null,
        dryRun,
        preview: dryRun ? null : result,
        onProgress: setProgress,
      }
      const r = await runner(args)
      setResult(r)
      /* A successful dry run is STAGED, never auto-applied. The number has to
       * be read by a person before anything moves. */
      if (r?.ok && dryRun) setStaged({ verb, at: Date.now() })
      else setStaged(null)
      if (r?.ok && !dryRun) onDone?.(issue, r)
    } catch (e) {
      setResult({ ok: false, note: `It threw: ${e?.message || e}. Nothing is assumed to have been written.` })
    } finally {
      setBusy(false); setProgress(null)
    }
  }

  return (
    <div className={styles.issue} data-sev={SEV[issue.severity] || 'warn'}>
      <p className={styles.head}>
        <span className={styles.label}>{issue.label}</span>
        {Number.isFinite(issue.count) && issue.count > 0 && (
          <span className={styles.count}>{issue.count.toLocaleString()}</span>
        )}
      </p>
      <p className={styles.sentence}>{issue.sentence}</p>

      <div className={styles.controls}>
        {issue.controls.map((c) => (
          <button
            key={c.verb}
            type="button"
            className={styles.cta}
            data-destructive={c.destructive ? '1' : '0'}
            aria-expanded={open === c.verb}
            onClick={() => { setOpen(open === c.verb ? null : c.verb); setResult(null); setStaged(null) }}
          >
            {c.cta}
          </button>
        ))}
      </div>

      {open && (() => {
        const c = issue.controls.find((x) => x.verb === open)
        if (c.verb === 'assign-rep') return <BodyRoster initiallyOpen entity={context.entity || { key: issue.bodyKey || 'issue-roster' }} rows={issue.records || context.records || []} />
        const needs = c.needs || []
        return (
          <div className={styles.form}>
            {/* What it writes and who may press it — stated before the press,
                not discovered from a 403 afterwards. */}
            <p className={styles.writes}>
              <b>Writes:</b> {c.writes}<br />
              <b>Who can:</b> {c.gate}
              {c.refuseWithout && <><br /><b>Refused without:</b> {c.refuseWithout}</>}
            </p>

            {needs.includes('source') && (
              <label className={styles.field}>
                <span>Source — the body&rsquo;s own page that confirms it</span>
                <input className={styles.input} inputMode="url" placeholder="https://…"
                  value={fields.source || ''} onChange={set('source')} />
              </label>
            )}
            {needs.includes('reason') && (
              <label className={styles.field}>
                <span>Why — written to the permanent ledger on every record</span>
                <input className={styles.input} placeholder="The ruling, in one line"
                  value={fields.reason || ''} onChange={set('reason')} />
              </label>
            )}
            {needs.includes('authorizedBy') && (
              <label className={styles.field}>
                <span>Authorised by — who DECIDED, which is not always who clicked</span>
                <input className={styles.input} placeholder="Name"
                  value={fields.authorizedBy || ''} onChange={set('authorizedBy')} />
              </label>
            )}
            {needs.includes('operation') && (() => {
              const name = fields.operation || issue.operation || ''
              const spec = (ops || []).find((o) => o.name === name)
              return (
                <p className={styles.writes}>
                  <b>Operation:</b> {name || '(none named)'}
                  {ops === null && ' — reading what it does from the server…'}
                  {ops !== null && !spec && name && ' — ⚠️ the server does not list this operation, so nothing would run.'}
                  {spec && <><br /><b>Touches:</b> {(spec.fields || []).join(', ')}<br />{spec.why}</>}
                </p>
              )
            })()}

            <div className={styles.actions}>
              {c.dryRunFirst ? (
                <>
                  <button type="button" className={styles.run} disabled={busy}
                    onClick={() => run(c.verb, true)}>
                    {busy ? 'Working…' : 'Show me what would change'}
                  </button>
                  {/* ⚠️ APPLY ONLY APPEARS AFTER A DRY RUN HAS BEEN READ. It is
                      not disabled-and-present: a greyed button invites the
                      click that the dry run exists to come before. */}
                  {staged?.verb === c.verb && (
                    <button type="button" className={`btn btn-primary ${styles.apply}`} disabled={busy}
                      onClick={() => run(c.verb, false)}>
                      {c.destructive ? `Apply it — ${c.label.toLowerCase()}` : 'Apply it'}
                    </button>
                  )}
                </>
              ) : (
                <button type="button" className={styles.run} disabled={busy}
                  onClick={() => run(c.verb, false)}>
                  {busy ? 'Working…' : c.cta}
                </button>
              )}
              <button type="button" className={styles.cancel} onClick={() => { setOpen(null); setResult(null); setStaged(null) }}>
                Close
              </button>
            </div>

            {progress && (
              <p className={styles.note}>{progress.done.toLocaleString()} of {progress.total.toLocaleString()}…</p>
            )}
            {/* ALWAYS a sentence. */}
            {result && (
              <p className={result.ok ? styles.note : styles.warn} role="status">
                {result.note}
                {result.hint ? ` ${result.hint}` : ''}
                {result.errors?.length ? ` First error: ${result.errors[0]}` : ''}
              </p>
            )}
          </div>
        )
      })()}
    </div>
  )
}
